/** Hex / contrast helpers for desk accent tokens. Companion can reuse these names. */

export type Rgb = { r: number; g: number; b: number };

const HEX3 = /^#?([0-9a-f]{3})$/i;
const HEX6 = /^#?([0-9a-f]{6})$/i;

export function parseHexColor(raw: unknown): Rgb | null {
  if (typeof raw !== "string") return null;
  const trimmed = raw.trim();
  const short = HEX3.exec(trimmed);
  if (short) {
    const [r, g, b] = short[1].split("");
    return {
      r: parseInt(r + r, 16),
      g: parseInt(g + g, 16),
      b: parseInt(b + b, 16),
    };
  }
  const full = HEX6.exec(trimmed);
  if (!full) return null;
  const h = full[1];
  return {
    r: parseInt(h.slice(0, 2), 16),
    g: parseInt(h.slice(2, 4), 16),
    b: parseInt(h.slice(4, 6), 16),
  };
}

export function formatHex(rgb: Rgb): string {
  const clamp = (n: number) => Math.max(0, Math.min(255, Math.round(n)));
  return `#${[clamp(rgb.r), clamp(rgb.g), clamp(rgb.b)]
    .map((n) => n.toString(16).padStart(2, "0"))
    .join("")}`;
}

export function normalizeHexColor(raw: unknown): string | null {
  const parsed = parseHexColor(raw);
  return parsed ? formatHex(parsed) : null;
}

function channelLinear(value: number): number {
  const s = value / 255;
  return s <= 0.04045 ? s / 12.92 : ((s + 0.055) / 1.055) ** 2.4;
}

/** WCAG relative luminance 0–1. */
export function relativeLuminance(hex: string): number {
  const rgb = parseHexColor(hex);
  if (!rgb) return 0;
  return 0.2126 * channelLinear(rgb.r) + 0.7152 * channelLinear(rgb.g) + 0.0722 * channelLinear(rgb.b);
}

export function contrastInk(hex: string, dark = "#1c1f23", light = "#f2f6fa"): string {
  return relativeLuminance(hex) > 0.45 ? dark : light;
}

export function mixHex(a: string, b: string, amount: number): string {
  const left = parseHexColor(a);
  const right = parseHexColor(b);
  if (!left || !right) return normalizeHexColor(a) ?? "#000000";
  const t = Math.max(0, Math.min(1, amount));
  return formatHex({
    r: left.r + (right.r - left.r) * t,
    g: left.g + (right.g - left.g) * t,
    b: left.b + (right.b - left.b) * t,
  });
}

export function darkenHex(hex: string, amount: number): string {
  return mixHex(hex, "#000000", amount);
}

export function lightenHex(hex: string, amount: number): string {
  return mixHex(hex, "#ffffff", amount);
}
